import React, { useEffect } from 'react';
import setSeo from '../utils/seo.js';
import { Link } from 'react-router-dom';
import PageHero3D from '../components/PageHero3D.jsx';
import BackToTop from '../components/BackToTop.jsx';
import case1 from '../assets/case1.webp';
import case2 from '../assets/case2.webp';
import case3 from '../assets/case3.webp';
import case4 from '../assets/case4.webp';

/**
 * CaseStudiesPage provides high‑level summaries of client success stories.
 * Each case study card includes an image, a brief description and a link
 * inviting visitors to learn more. Additional case studies can be added
 * easily by extending the `studies` array.
 */
const studies = [
  {
    id: 1,
    title: 'Healthcare Industry – AI-Powered Appointment Scheduling System',
    excerpt: 'An AI-driven scheduling agent that reduced scheduling conflicts by 70% and appointment confirmations from hours to under 1 minute.',
    image: case1,
    slug: '/case-studies/healthcare-scheduling'
  },
  {
    id: 2,
    title: 'Entertainment Industry – Custom Artist Management CRM',
    excerpt: 'A tailored CRM solution for managing artists, shows, and concerts that increased audience engagement by 30% and reduced coordination efforts by 50%.',
    image: case2,
    slug: '/case-studies/entertainment-crm'
  },
  {
    id: 3,
    title: 'Marketing Industry – LinkedIn Automation with AI Feedback Loops',
    excerpt: 'An automated LinkedIn marketing system that reduced campaign preparation from 2 days to 2 hours and improved lead qualification accuracy by 40%.',
    image: case3,
    slug: '/case-studies/linkedin-automation'
  },
  {
    id: 4,
    title: 'Media & Photography Industry – AI Orchestrator for Finance Workflows',
    excerpt: 'An AI-powered finance automation system that reduced manual invoice work by 80% with 100% data privacy through offline processing.',
    image: case4,
    slug: '/case-studies/finance-automation'
  }
];

const CaseStudiesPage = () => {
  useEffect(() => {
    setSeo({
      title: 'Case Studies | Aakeeo Technologies - Real Business Transformations',
      description: 'Explore how we\'ve helped businesses transform with AI automation, CRM solutions, and custom software. Real results from healthcare, entertainment, marketing, and finance industries.',
      keywords: ['case studies', 'AI automation success stories', 'CRM implementation results', 'business transformation', 'customer success stories', 'healthcare automation', 'CRM case studies'],
      og: {
        title: 'Success Stories & Case Studies | Aakeeo Technologies',
        description: 'See how businesses achieved success with our AI, CRM, and software solutions. Real transformations, measurable results.',
        type: 'website'
      },
      canonical: window.location.href,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': 'Case Studies',
        'description': 'Real business transformation case studies from Aakeeo Technologies',
        'url': 'https://aakeeo.com/case-studies',
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies'
        }
      }
    });
  }, []);

  return (
    <div className="case-studies-page">
      <PageHero3D 
        title="Case Studies" 
        subtitle="Real results from businesses that trusted Aakeeo to transform their processes and unlock growth." 
        accent="#023e8a"
      />
      <section className="section reveal">
        <div className="section-header">
          <h2>Success Stories</h2>
          <p>See how our custom CRM and AI solutions have made a tangible impact across different industries.</p>
        </div>
        <div className="blog-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
          {studies.map(study => (
            <article key={study.id} className="blog-card" style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '20px', overflow: 'hidden', backdropFilter: 'blur(8px)', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', transition: 'transform 0.2s ease' }}>
              <div style={{ height: '180px', overflow: 'hidden' }}>
                <img src={study.image} alt={study.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.2rem', color: 'var(--secondary-color)', marginBottom: '0.5rem' }}>{study.title}</h3>
                <p style={{ color: 'var(--text-light)', fontSize: '0.95rem', marginBottom: '1rem' }}>{study.excerpt}</p>
                <Link 
                  to={study.slug} 
                  className="btn-primary" 
                  style={{ fontSize: '0.9rem', padding: '0.6rem 1.4rem' }}
                >
                  Read More
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
      <BackToTop />
    </div>
  );
};

export default CaseStudiesPage;
